import { Box, Fade } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useState } from "react"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import CircularLoadingIndicator from "@/components/common/LoadingIndicators/CircularLoadingIndicator"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import CompanyFilesSection from "@/screens/Companies/edit/components/info/components/CompanyFiles/CompanyFilesSection"
import { CompanyFilesProvider } from "@/screens/Companies/edit/components/info/context/CompanyFilesContext"
import AddMoreFilesModal from "@/screens/Companies/edit/components/info/dialogs/AddMoreFilesModal"
import CompanyFileDialog from "@/screens/Companies/edit/components/info/dialogs/CompanyFileDialog"

enum DialogMode {
  Upload = "upload",
  UploadMore = "uploadMore",
}

const CompanyFilesContainer: FC<{
  companyId: string
  isActiveCompany: boolean
}> = ({ companyId, isActiveCompany }) => {
  const { fileList, fileFetching, refetchFileUploadData } = useGetFileUploadData({
    referenceTableName: ReferenceTableName.Company,
    referenceId: companyId,
  })

  const [openedDialog, setOpenedDialog] = useState<DialogMode | null>(null)

  const handleFileUploadDialogOpen = () => {
    setOpenedDialog(DialogMode.Upload)
  }

  const handleDialogClose = () => {
    setOpenedDialog(null)
  }

  const onRefresh = useCallback(async () => {
    refetchFileUploadData()
  }, [refetchFileUploadData])

  const hasData = fileList.length > 0
  const noData = fileList.length === 0 && !fileFetching

  return (
    <Box className='my-8'>
      {hasData && (
        <CompanyFilesProvider isActiveCompany={isActiveCompany} onRefresh={onRefresh}>
          <CompanyFilesSection fileList={fileList} openFileUploadDialog={handleFileUploadDialogOpen} />
        </CompanyFilesProvider>
      )}
      {fileFetching && <CircularLoadingIndicator />}
      {noData && (
        <Fade in={true} unmountOnExit timeout={500}>
          <InformationMissingAlert
            title='Upload Files'
            description='PDF, PNG, JPG or DOC (max. 10MB)'
            action={
              <Box className='flex h-full items-center'>
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<AddOutlined />}
                  onClick={handleFileUploadDialogOpen}
                >
                  Upload Files
                </Button>
              </Box>
            }
          />
        </Fade>
      )}
      {openedDialog === DialogMode.Upload && (
        <CompanyFileDialog
          companyId={companyId}
          onClose={handleDialogClose}
          onSuccess={() => {
            setOpenedDialog(DialogMode.UploadMore)
            onRefresh()
          }}
        />
      )}
      {openedDialog === DialogMode.UploadMore && (
        <AddMoreFilesModal
          onSubmit={() => {
            setOpenedDialog(DialogMode.Upload)
          }}
          onClose={handleDialogClose}
        />
      )}
    </Box>
  )
}

export default CompanyFilesContainer
